import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { TranslateService } from '@ngx-translate/core';
import { AdminAlertsService } from 'app/admin/alerts/admin-alerts.service';
import { CompleteProfileModalComponent } from 'app/core-lib/dialogs/complete-profile-modal/complete-profile-modal.component';
import { ModalService } from 'app/core-lib/dialogs/modal/modal.service';
import { NotificationsService } from 'app/notifications/notifications.service';
import { ProfileService } from 'app/configuration/profile/profile.service';
import { CustomerService } from 'app/shared/customer.service';
import { FilterDateService } from 'app/shared/filters/date/filter-date-service';
import { Hotel } from 'app/shared/model/hotel';
import { User } from 'app/shared/model/user.model';
import { UtilService } from 'app/shared/util.service';
import { Observable, forkJoin, from, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthenticationService, SessionService } from '../../_services/index';
import { SessionTokens } from './auth-flow.model';

const MY_HOTEL_ID = 12;
const NPS_SCALE_DEFAULT = { detractor: 3.25, promoter: 4.25 };

export interface PostLoginContext {
  returnUrl: string;
  queryParams: Record<string, any>;
  customerParam?: string;
}

@Injectable()
export class PostLoginService {
  private readonly router = inject(Router);
  private readonly sessionService = inject(SessionService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly utilService = inject(UtilService);
  private readonly translate = inject(TranslateService);
  private readonly customerService = inject(CustomerService);
  private readonly profileService = inject(ProfileService);
  private readonly adminAlertsService = inject(AdminAlertsService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly datesFilter = inject(FilterDateService);
  private readonly modalService = inject(ModalService);

  private readonly jwtHelper = new JwtHelperService();

  persistSession(tokens: SessionTokens): void {
    localStorage.setItem('currentJWT', JSON.stringify(tokens));
    localStorage.setItem('shouldShowProfileCompletionModal', JSON.stringify(!!tokens.profile_completeness_show_modal));
    this.sessionService.removeCurrentHotelPSConfig();
  }

  run(tokens: SessionTokens, context: PostLoginContext): Observable<unknown> {
    this.persistSession(tokens);

    const data = this.jwtHelper.decodeToken(tokens.token);

    return this.authenticationService.profile(data.user_details.id).pipe(
      map((profile: any[]) => User.createFromApi(profile)),
      tap((currentUser: User) => this.checkLastHotelUsed(currentUser, context)),
      switchMap((currentUser: User) =>
        this.getUserProperties(currentUser, context).pipe(
          switchMap(() => from(this.finish(currentUser, tokens, context))),
        ),
      ),
      tap(() => this.utilService.ga('login', 'click', 'success')),
      catchError((error) => {
        this.authenticationService.logout();
        return throwError(() => error);
      }),
    );
  }

  private async finish(currentUser: User, tokens: SessionTokens, context: PostLoginContext): Promise<void> {
    const currentHotel = this.getLastHotelFromStorage();
    if (!currentHotel?.id) throw new Error('[login] no hotel available for the current user');

    const customerId = currentHotel.id.toString();

    if (this.utilService.getAferCustomerId() !== customerId) {
      this.adminAlertsService.getAlert(customerId, this.utilService.getLanguageId()).subscribe((response) => {
        this.notificationsService.setResponseAlert(response);
      });
      this.utilService.setAfterCustomerId(customerId);
    }

    this.utilService.clearChache();
    this.utilService.currentUser = currentUser;
    localStorage.setItem('currentUser', JSON.stringify(currentUser));
    this.datesFilter.initDates();

    if (tokens.profile_completeness_show_modal) this.openProfileCompletionModal(currentUser, tokens);

    const fullUrl = this.buildDestination(context);
    const hasAccess = await this.utilService.customerChanged(currentHotel, !currentUser.isSuperAdmin(), fullUrl);
    if (hasAccess) this.router.navigateByUrl(fullUrl);

    this.utilService.setCurrrentLanguage(currentUser.language);
    this.utilService.refreshHeader();
    this.utilService.ga('login', 'login', 'login_user_id', currentUser.id.toString());
  }

  private openProfileCompletionModal(currentUser: User, tokens: SessionTokens): void {
    const username = currentUser.name || this.translate.instant('commons.user');
    const percentage = tokens.profile_completeness?.percentage || 0;

    setTimeout(() => {
      this.modalService.open(CompleteProfileModalComponent, { data: { username, percentage } }, [
        'overlay-panel',
        'md',
        'no-padding',
      ]);
    }, 600);
  }

  private buildDestination(context: PostLoginContext): string {
    const entries = Object.entries(context.queryParams);
    if (!entries.length) return context.returnUrl;

    const query = entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v ?? ''))}`).join('&');
    const separator = context.returnUrl.includes('?') ? '&' : '?';
    return `${context.returnUrl}${separator}${query}`;
  }

  private getUserProperties(user: User, context: PostLoginContext): Observable<unknown> {
    const currentHotel = this.getLastHotelFromStorage();
    const hotelId = Number(context.customerParam) || Number(currentHotel?.id) || MY_HOTEL_ID;

    const superAdminObservables = user.isSuperAdmin()
      ? [this.profileService.getSuperAdminData(user.id.toString())]
      : [];

    return forkJoin([
      this.authenticationService.fidelityConfiguration(hotelId),
      this.customerService.findHotel(hotelId),
      ...superAdminObservables,
    ]).pipe(
      map(([fidelityConfig, rawHotel, adminData]) => {
        user.superAdminProperties = adminData && adminData[0];

        if (hotelId !== MY_HOTEL_ID && rawHotel && rawHotel['id']) {
          const hotel = Hotel.createFromApi(rawHotel);
          if (Number(currentHotel?.id) !== Number(hotel.id)) this.setLastHotelUsed(hotel);
        }

        this.utilService.setScoreNps(NPS_SCALE_DEFAULT);
        this.sessionService.setFidelityProperties(fidelityConfig);
      }),
    );
  }

  private checkLastHotelUsed(currentUser: User, context: PostLoginContext): void {
    const lastHotel: Hotel = this.getLastHotelFromStorage();
    const hotelToSet: Hotel =
      lastHotel && this.userHasAccess(currentUser, lastHotel) ? lastHotel : currentUser.getAccessHotels()[0];

    const hotelFromParams = currentUser
      .getAccessHotels()
      .find((x) => parseInt(x.id) === parseInt(context.customerParam));

    this.setLastHotelUsed(hotelFromParams || hotelToSet);
  }

  private userHasAccess(currentUser: User, lastHotel: Hotel): boolean {
    return (
      currentUser.isSuperAdmin() ||
      !!currentUser.getAccessHotels().some((x: Hotel) => parseInt(x.id) === parseInt(lastHotel.id))
    );
  }

  private getLastHotelFromStorage() {
    try {
      return JSON.parse(localStorage.getItem('lastHotelUsed'));
    } catch (e) {
      localStorage.removeItem('lastHotelUsed');
      return null;
    }
  }

  private setLastHotelUsed(hotel) {
    if (!hotel) return;
    localStorage.setItem('lastHotelUsed', JSON.stringify(hotel));
  }
}
